import React, { useEffect, useState, useMemo } from "react";
import AppLayout from "../components/AppLayout";
import { api, errMsg, formatCurrency } from "../lib/api";
import { Plus, Trash2, TrendingUp, DollarSign, AlertTriangle, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { exportRollupPDF } from "../lib/rollupPdf";
import OwnerSelect from "../components/OwnerSelect";

export default function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pg, s, tm] = await Promise.all([api.get("/programs"), api.get("/analytics/project-summary"), api.get("/teammates")]);
      setPrograms(pg.data); setSummaries(s.data); setTeammates(tm.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const { data } = await api.post("/programs", { data: { name: "New Program", owner: "", description: "", budget: 0 }});
      setPrograms((x) => [data, ...x]);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const update = async (id, patch) => {
    setPrograms((xs) => xs.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    try { await api.put(`/programs/${id}`, { data: patch }); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete program?")) return;
    try { await api.delete(`/programs/${id}`); setPrograms((xs) => xs.filter((p) => p.id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadRollup = async (id, name) => {
    try {
      toast.message(`Building ${name} rollup…`);
      const { data } = await api.get(`/analytics/rollup/program/${id}`);
      exportRollupPDF(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rollup = useMemo(() => (programId) => {
    const projects = summaries.filter((p) => p.program_id === programId);
    const est = projects.reduce((a, p) => a + p.estimated_cost, 0);
    const actual = projects.reduce((a, p) => a + p.actual_cost, 0);
    return { est, actual, projectCount: projects.length };
  }, [summaries]);

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Programs</h1>
          <p className="text-slate-400 mt-1">Group related projects. Budgets, estimates & actuals roll up live.</p>
        </div>
        <button onClick={add} data-testid="add-program-btn" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"><Plus size={14}/> New program</button>
      </div>

      {loading ? <div className="text-slate-400">Loading…</div>
        : programs.length === 0 ? <div className="pc-card p-10 text-center text-slate-400">No programs yet.</div>
        : <div className="space-y-4" data-testid="programs-list">
            {programs.map((p) => {
              const r = rollup(p.id);
              const budget = Number(p.budget || 0);
              const estVar = r.est - budget;
              const actualVar = r.actual - budget;
              const overBudget = actualVar > 0;
              return (
                <div key={p.id} className="pc-card p-5" data-testid={`program-${p.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <input value={p.name || ""} onChange={(e) => update(p.id, { name: e.target.value })} className="flex-1 text-lg font-semibold text-white bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 outline-none" style={{ fontFamily: "Outfit" }} />
                    <button onClick={() => downloadRollup(p.id, p.name)} data-testid={`program-rollup-pdf-${p.id}`} className="inline-flex items-center gap-1 border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-1.5 rounded-lg text-xs"><FileDown size={12}/> Rollup PDF</button>
                    <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-rose-400"><Trash2 size={14}/></button>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 mb-3">
                    <OwnerSelect teammates={teammates} value={p.owner_teammate_id} legacy={p.owner} testId={`program-owner-${p.id}`}
                      onChange={(v) => update(p.id, { owner_teammate_id: v, owner: teammates.find((t) => t.id === v)?.name || "" })} />
                    <input placeholder="Description" value={p.description || ""} onChange={(e) => update(p.id, { description: e.target.value })} className="sm:col-span-2 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`program-rollup-${p.id}`}>
                    <Card label="Budget" value={formatCurrency(budget)} icon={DollarSign} editable>
                      <input type="number" value={p.budget || 0} onChange={(e) => update(p.id, { budget: Number(e.target.value || 0) })} className="w-full px-2 py-1 rounded border focus:outline-none focus:border-blue-500 text-sm mt-1"/>
                    </Card>
                    <Card label="Cost Est" value={formatCurrency(r.est)} sub={estVar === 0 ? "On budget" : estVar > 0 ? `${formatCurrency(estVar)} over` : `${formatCurrency(Math.abs(estVar))} under`}
                      icon={TrendingUp} tone={estVar > 0 ? "rose" : "emerald"} />
                    <Card label="Actual Cost" value={formatCurrency(r.actual)} sub={budget === 0 ? `${r.projectCount} projects` : overBudget ? `${formatCurrency(actualVar)} over` : `${formatCurrency(Math.abs(actualVar))} under`}
                      icon={overBudget ? AlertTriangle : CheckCircle2} tone={overBudget ? "rose" : "emerald"} />
                    <Card label="Projects" value={r.projectCount} sub="in this program" icon={DollarSign} />
                  </div>
                </div>
              );
            })}
          </div>}
    </AppLayout>
  );
}

function Card({ label, value, sub, icon: Icon, tone, editable, children }) {
  const toneCls = tone === "rose" ? "text-rose-300" : tone === "emerald" ? "text-emerald-300" : "text-white";
  return (
    <div className={`rounded-lg bg-slate-900/40 border border-slate-800 p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-blue-300/80">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className={`text-lg font-semibold mt-1 ${toneCls}`} style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      {editable && children}
    </div>
  );
}
